import * as React from "react";

export enum PRESENTATION_SCROLL_ACTIONS {
  NONE = "NONE",
  SCROLL_TO_TOP = "SCROLL_TO_TOP",
  SCROLL_TO_FORM_ERROR_FIELD = "SCROLL_TO_FORM_ERROR_FIELD",
}

export type PresentationScrollAction = `${PRESENTATION_SCROLL_ACTIONS}`;

interface PresenterForScrollConsumerInterface {
  scrollActionToTake: PresentationScrollAction;
  onScrollAnimationBegin: () => void;
}

interface PresenterForScrollConsumer_ControllerInterface {
  initiateScrollAction: (action: PresentationScrollAction) => void;
}

const defaultValue = {
  scrollActionToTake: PRESENTATION_SCROLL_ACTIONS.NONE,
} as unknown as PresenterForScrollConsumerInterface;
const PresenterForScrollContext = React.createContext(defaultValue);

const defaultControllerValue =
  {} as unknown as PresenterForScrollConsumer_ControllerInterface;
const PresenterForScrollControllerContext = React.createContext(
  defaultControllerValue
);

export const PresenterForScrollProvider: React.FunctionComponent = (props) => {
  const [scrollActionToTake, setScrollActionToTake] =
    React.useState<PresentationScrollAction>(PRESENTATION_SCROLL_ACTIONS.NONE);

  const initiateScrollAction = (action: PresentationScrollAction) => {
    setScrollActionToTake(action);
  };

  const onScrollAnimationBegin = () => {
    setScrollActionToTake(PRESENTATION_SCROLL_ACTIONS.NONE);
  };

  const value: PresenterForScrollConsumerInterface = React.useMemo(
    () => ({
      scrollActionToTake,
      onScrollAnimationBegin,
    }),
    [scrollActionToTake, onScrollAnimationBegin]
  );

  const controllerValue: PresenterForScrollConsumer_ControllerInterface =
    React.useMemo(
      () => ({
        initiateScrollAction,
      }),
      [initiateScrollAction]
    );

  return (
    <PresenterForScrollContext.Provider value={value}>
      <PresenterForScrollControllerContext.Provider value={controllerValue}>
        {props.children}
      </PresenterForScrollControllerContext.Provider>
    </PresenterForScrollContext.Provider>
  );
};

export const usePresenterForScroll = () => {
  const context = React.useContext(PresenterForScrollContext);
  if (context === defaultValue) {
    throw new Error(
      "usePresenterForScroll must be used within a <PresenterForScrollProvider>"
    );
  }
  return context;
};

export const usePresenterForScroll_asController = () => {
  const context = React.useContext(PresenterForScrollControllerContext);
  if (context === defaultControllerValue) {
    throw new Error(
      "usePresenterForScroll_asController must be used within a <PresenterForScrollProvider>"
    );
  }
  return context;
};
